import { useEffect, useRef, type ReactNode, type SyntheticEvent } from "react";
import { Copy, Download, FolderLock, Forward, Image, Pencil, Reply, Trash2 } from "lucide-react";
import { NotoReactionFace, QUICK_REACTIONS } from "@/lib/chatNotoEmoji";
import { ChatQuickReactionStrip } from "./ChatQuickReactionStrip";

export { QUICK_REACTIONS };

type Props = {
  x: number;
  y: number;
  canEdit: boolean;
  canDelete: boolean;
  myReaction?: string | null;
  /** Tiroir Messenger (mobile / appui long) vs menu flottant (desktop). */
  sheet?: boolean;
  /** Timestamp d’ouverture — ignore les touch/click fantômes juste après l’appui long */
  openedAt?: number;
  onReact: (emoji: string) => void;
  onReply: () => void;
  onCopy: () => void;
  onForward: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
  hasFiles?: boolean;
  hasImages?: boolean;
  onDownload?: () => void;
  onSaveCoffre?: () => void;
  onSavePhotos?: () => void;
};

export function MessageContextMenu({
  x,
  y,
  canEdit,
  canDelete,
  myReaction,
  sheet = false,
  openedAt = 0,
  onReact,
  onReply,
  onCopy,
  onForward,
  onEdit,
  onDelete,
  onClose,
  hasFiles = false,
  hasImages = false,
  onDownload,
  onSaveCoffre,
  onSavePhotos,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const guardMs = 480;
    const onDoc = (e: Event) => {
      if (Date.now() - openedAt < guardMs) return;
      const target = e.target as Node | null;
      if (target && ref.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onDoc, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, openedAt]);

  useEffect(() => {
    if (!sheet) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sheet]);

  const armAction = (fn: () => void) => (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fn();
  };

  const actions = (
    <>
      <SheetOrMenuButton
        sheet={sheet}
        icon={<Reply className="size-5" />}
        label="Répondre"
        onPick={armAction(onReply)}
      />
      <SheetOrMenuButton
        sheet={sheet}
        icon={<Forward className="size-5" />}
        label="Transférer"
        onPick={armAction(onForward)}
      />
      <SheetOrMenuButton
        sheet={sheet}
        icon={<Copy className="size-5" />}
        label="Copier"
        onPick={armAction(onCopy)}
      />
      {canEdit ? (
        <SheetOrMenuButton
          sheet={sheet}
          icon={<Pencil className="size-5" />}
          label="Modifier"
          onPick={armAction(onEdit)}
        />
      ) : null}
      {canDelete ? (
        <SheetOrMenuButton
          sheet={sheet}
          icon={<Trash2 className="size-5" />}
          label="Supprimer"
          danger
          onPick={armAction(onDelete)}
        />
      ) : null}
    </>
  );

  const fileActions = (
    <>
      {hasFiles && onDownload ? (
        <SheetOrMenuButton
          sheet={sheet}
          icon={<Download className="size-5" />}
          label="Télécharger"
          onPick={armAction(onDownload)}
        />
      ) : null}
      {hasFiles && onSaveCoffre ? (
        <SheetOrMenuButton
          sheet={sheet}
          icon={<FolderLock className="size-5" />}
          label="Coffre"
          onPick={armAction(onSaveCoffre)}
        />
      ) : null}
      {hasImages && onSavePhotos ? (
        <SheetOrMenuButton
          sheet={sheet}
          icon={<Image className="size-5" />}
          label="Photos"
          onPick={armAction(onSavePhotos)}
        />
      ) : null}
    </>
  );

  if (sheet) {
    const vw = typeof window !== "undefined" ? window.innerWidth : 390;
    const emojiW = Math.min(vw - 20, 360);
    const vh = typeof window !== "undefined" ? window.innerHeight : 700;
    const emojiLeft = Math.min(Math.max(10, x - emojiW / 2), vw - emojiW - 10);
    const emojiTop = y < 96 ? Math.min(y + 36, vh - 220) : Math.max(10, y - 76);

    return (
      <div
        ref={ref}
        className="chat-msg-sheet-root"
        role="presentation"
        onContextMenu={(e) => e.preventDefault()}
      >
        <button
          type="button"
          className="chat-msg-sheet-backdrop"
          aria-label="Fermer"
          onPointerDown={(e) => {
            if (Date.now() - openedAt < 480) return;
            e.preventDefault();
            onClose();
          }}
        />
        <div
          className="chat-msg-sheet-emojis"
          style={{ left: emojiLeft, top: emojiTop, width: emojiW }}
          role="toolbar"
          aria-label="Réactions rapides"
        >
          <ChatQuickReactionStrip myReaction={myReaction} openedAt={openedAt} onReact={onReact} />
        </div>
        <div className="chat-msg-sheet" role="menu" aria-label="Actions du message">
          <div className="chat-msg-sheet__handle" aria-hidden />
          {hasFiles && (canEdit || canDelete) ? (
            <>
              <div className="chat-msg-sheet__row">{actions}</div>
              <div className="chat-msg-sheet__row chat-msg-sheet__row--files">{fileActions}</div>
            </>
          ) : (
            <div className="chat-msg-sheet__row">
              {actions}
              {fileActions}
            </div>
          )}
        </div>
      </div>
    );
  }

  const left = Math.min(
    Math.max(8, x - 20),
    typeof window !== "undefined" ? window.innerWidth - 380 : x
  );
  const top = Math.min(
    Math.max(8, y - 8),
    typeof window !== "undefined" ? window.innerHeight - 140 : y
  );

  return (
    <div
      ref={ref}
      className="fixed z-[80] w-[min(22.5rem,calc(100vw-1rem))] overflow-hidden rounded-2xl border border-[var(--chat-border)] bg-[var(--chat-surface)] shadow-[var(--chat-shadow)]"
      style={{ left, top }}
      role="menu"
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="px-1 pt-1.5">
        <ChatQuickReactionStrip myReaction={myReaction} openedAt={openedAt} onReact={onReact} />
      </div>
      <div className="flex items-center justify-center gap-0.5 border-t border-[var(--chat-border)] px-1.5 py-1.5">
        {actions}
        {fileActions}
      </div>
    </div>
  );
}

function SheetOrMenuButton({
  sheet,
  icon,
  label,
  danger,
  onPick,
}: {
  sheet: boolean;
  icon: ReactNode;
  label: string;
  danger?: boolean;
  onPick: (e: SyntheticEvent) => void;
}) {
  if (sheet) {
    return (
      <button
        type="button"
        role="menuitem"
        className={`chat-msg-sheet__item ${danger ? "is-danger" : ""}`}
        onPointerDown={onPick}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <span className="chat-msg-sheet__icon">{icon}</span>
        {label}
      </button>
    );
  }
  return (
    <button
      type="button"
      role="menuitem"
      title={label}
      aria-label={label}
      className={`flex size-10 items-center justify-center rounded-full transition active:scale-95 hover:bg-[var(--chat-surface-elevated)] ${
        danger ? "text-[var(--chat-danger)]" : "text-[var(--chat-muted)] hover:text-[var(--chat-text)]"
      }`}
      onPointerDown={onPick}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {icon}
    </button>
  );
}

/** Pastilles de réactions — sous le bord, côté extérieur de la bulle */
export function MessageReactionChips({
  reactions,
  selfId,
  align = "end",
  onToggle,
}: {
  reactions?: { emoji: string; count: number; user_ids: number[] }[];
  selfId: number;
  /** Messages envoyés = end (droite), reçus = start (gauche) */
  align?: "start" | "end";
  onToggle: (emoji: string) => void;
}) {
  if (!reactions || reactions.length === 0) return null;
  const atEnd = align === "end";
  return (
    <div
      className={`chat-reaction-chips absolute -bottom-3.5 z-[5] flex max-w-[100%] flex-wrap gap-0.5 ${
        atEnd ? "left-auto right-0 justify-end" : "right-auto left-0 justify-start"
      }`}
    >
      {reactions.map((r) => {
        const mine = r.user_ids.includes(selfId);
        return (
          <button
            key={r.emoji}
            type="button"
            className={`chat-reaction-chip relative inline-flex size-6 shrink-0 items-center justify-center rounded-full border shadow-sm backdrop-blur-sm transition hover:scale-105 ${
              mine
                ? "border-[var(--chat-accent)] bg-[var(--chat-accent-soft)]"
                : "border-[var(--chat-border)] bg-[var(--chat-surface)]"
            }`}
            title={mine ? "Retirer" : "Réagir aussi"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggle(r.emoji);
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            <span className="chat-reaction-chip__emoji leading-none">
              <NotoReactionFace emoji={r.emoji} className="chat-noto-react chat-noto-react--chip" />
            </span>
            {r.count > 1 ? (
              <span className="chat-reaction-chip__count" aria-label={`${r.count} réactions`}>
                {r.count > 9 ? "9+" : r.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
